import { jsPDF } from "jspdf";
import { formatDate } from "./format";

// Catalogo completo dei campi disponibili per la Copertina Pratica.
export const COPERTINA_CATALOG = [
  { key: "numero_pratica", label: "N. Pratica" },
  { key: "tipo", label: "Tipo Pratica" },
  { key: "assistito", label: "Assistito" },
  { key: "targa", label: "Targa" },
  { key: "compagnia", label: "Compagnia" },
  { key: "data_sinistro", label: "Data Sinistro" },
  { key: "luogo_sinistro", label: "Luogo Sinistro" },
  { key: "stato", label: "Stato" },
  { key: "stato_aggiuntivo", label: "Stato Aggiuntivo" },
  { key: "segnalatore", label: "Segnalatore" },
  { key: "cessionario", label: "Cessionario" },
  { key: "assistito_num_sinistro", label: "N. Sinistro" },
  { key: "gestionaria_num_sinistro", label: "N. Sinistro Gestionaria" },
  { key: "assistito_liquidatore", label: "Liquidatore" },
  { key: "assistito_perito_compagnia", label: "Perito" },
  { key: "ispettorato", label: "Ispettorato" },
  { key: "assistito_medico_legale", label: "Medico Legale" },
  { key: "legale", label: "Legale" },
  { key: "controparte", label: "Controparte" },
  { key: "cp_compagnia", label: "Compagnia Controparte" },
];

export const DEFAULT_COPERTINA_FIELDS = [
  "numero_pratica", "tipo", "assistito", "targa", "compagnia", "data_sinistro", "luogo_sinistro", "stato",
];

const STATO_LABEL = { aperta: "Aperta", in_lavorazione: "In Lavorazione", legale: "Legale", mediazione: "Mediazione", chiusa: "Chiusa", out: "S.S." };

const primaryName = (p) => {
  const a = (p.assistiti && p.assistiti[0]) || {};
  return p.nome_assistito || a.nome_assistito || a.assistito_rs || a.denominazione || [a.cognome, a.nome].filter(Boolean).join(" ") || "";
};

// Iniziali puntate: "DANIELA LO BIANCO" -> "D.L.B."
const initialsOf = (name) =>
  (name || "").trim().split(/\s+/).filter(Boolean).map((t) => t[0].toUpperCase() + ".").join("");

// Nome del cessionario dalla scheda assistito (principale o tra gli assistiti).
const cessionarioName = (p) => {
  const cands = [];
  const sched = (p.assistito_scheda && typeof p.assistito_scheda === "object") ? p.assistito_scheda : {};
  if (sched.cessionario && typeof sched.cessionario === "object") cands.push(sched.cessionario);
  (p.assistiti || []).forEach((a) => { if (a && typeof a.cessionario === "object" && a.cessionario) cands.push(a.cessionario); });
  const c = cands.find((x) => x && Object.values(x).some((v) => String(v || "").trim())) || {};
  return c.ragione_sociale || c.titolare || "";
};

// La compagnia "gestionaria" dipende dalla procedura CARD: se CARD/DOPPIA FIRMA/SI -> lato assistito, altrimenti lato controparte.
const gestioneUsesAssistito = (p) => {
  const card = String(p.procedura_card || "").toUpperCase();
  return card === "CARD" || card === "DOPPIA FIRMA" || card === "SI";
};

export function copertinaValue(p, key) {
  const a = (p.assistiti && p.assistiti[0]) || {};
  const cp = (p.controparti && p.controparti[0]) || {};
  switch (key) {
    case "numero_pratica": return p.numero_pratica || "";
    case "tipo": return p.tipo || "";
    case "assistito": return primaryName(p);
    case "targa": return p.targa || a.targa || "";
    case "compagnia": return gestioneUsesAssistito(p) ? (p.compagnia || p.assistito_compagnia || a.assicurazione || a.compagnia || "") : (cp.compagnia || p.cp_compagnia || "");
    case "data_sinistro": return p.data_sinistro ? formatDate(p.data_sinistro) : "";
    case "luogo_sinistro": return p.luogo_sinistro || "";
    case "stato": return STATO_LABEL[p.stato] || p.stato || "";
    case "stato_aggiuntivo": return p.stato_aggiuntivo || "";
    case "segnalatore": {
      const nm = p.segnalatore || "";
      return (p._segnalatore_iniziali && nm) ? initialsOf(nm) : nm;
    }
    case "cessionario": {
      const nm = cessionarioName(p);
      return (p._cessionario_iniziali && nm) ? initialsOf(nm) : nm;
    }
    case "assistito_num_sinistro": return gestioneUsesAssistito(p) ? (p.assistito_num_sinistro || "") : (cp.num_sinistro || "");
    case "gestionaria_num_sinistro": return gestioneUsesAssistito(p) ? (p.assistito_num_sinistro || "") : (cp.num_sinistro || "");
    case "assistito_liquidatore": return gestioneUsesAssistito(p) ? (p.assistito_liquidatore || "") : (cp.liquidatore || "");
    case "assistito_perito_compagnia": return gestioneUsesAssistito(p) ? (p.assistito_perito_compagnia || p.assistito_perito_parte || "") : (cp.perito_compagnia || cp.perito || "");
    case "ispettorato": return gestioneUsesAssistito(p) ? (p.assistito_ispettorato || p.ispettorato || "") : (cp.ispettorato || "");
    case "assistito_medico_legale": return p.assistito_medico_legale || "";
    case "legale": return p.assistito_legale || p.legale || "";
    case "controparte": return cp.ragione_sociale || cp.denominazione || "";
    case "cp_compagnia": return cp.compagnia || p.cp_compagnia || "";
    default: return "";
  }
}

const labelOf = (key) => (COPERTINA_CATALOG.find((c) => c.key === key) || {}).label || key;

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const COP_SIZES = [8, 10, 12, 14, 16];

// Genera e scarica direttamente il PDF A4 della copertina (nessun popup di stampa).
function buildDoc(p, studio = {}, fieldKeys, margins, fieldStyles, pecDate) {
  const styles = (fieldStyles && typeof fieldStyles === "object") ? fieldStyles : {};
  const keys = (Array.isArray(fieldKeys) && fieldKeys.length ? fieldKeys : DEFAULT_COPERTINA_FIELDS).filter((k) => COPERTINA_CATALOG.some((c) => c.key === k));
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, H = 297;
  const M = Math.max(8, Math.min(30, Number(margins && margins.left) || 16));
  const cw = W - M * 2;
  doc.setDrawColor(0); doc.setTextColor(0);

  // ---- Intestazione studio rimossa su richiesta: la copertina parte dal margine superiore ----
  let y = M;

  // ---- Numero pratica ruotato sul bordo sinistro ----
  const numero = p.numero_pratica || "";
  if (numero) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(150);
    doc.text(numero, 9, H / 2, { angle: 90, align: "center" });
    doc.setTextColor(0);
  }

  // ---- PEC del __/__/____ (compilabile manualmente prima della stampa) ----
  {
    const pd = pecDate || {};
    const dd = String(pd.d || "").trim() || "____";
    const mm2 = String(pd.m || "").trim() || "_____";
    const yy = String(pd.y || "").trim() || "__________";
    doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text(`PEC del ${dd}/${mm2}/${yy}`, M, y + 4);
    y += 8;
  }

  // ---- Box PRATICA N. ----
  doc.setLineWidth(0.4);
  doc.rect(M, y, cw, 12);
  doc.setFont("helvetica", "bold"); doc.setFontSize(13);
  doc.text(`PRATICA N. ${numero || "—"}`, W / 2, y + 8, { align: "center" });
  y += 12;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  const dsx = p.data_sinistro ? formatDate(p.data_sinistro) : "—";
  doc.text(`SINISTRO DEL ${dsx}`, W / 2, y + 6.5, { align: "center" });
  y += 11;

  // ---- Riquadri CLIENTI / CONTROPARTI ----
  const boxW = (cw - 6) / 2;
  let boxH = 54;
  const drawParties = (x, title, rows) => {
    doc.setLineWidth(0.4); doc.rect(x, y, boxW, boxH);
    doc.setFillColor(0); doc.rect(x, y, boxW, 6, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(255);
    doc.text(title, x + 2.5, y + 4.2); doc.setTextColor(0);
    doc.setFontSize(8.5);
    let ry = y + 10.5;
    (rows.length ? rows : [{ t: "—" }]).slice(0, 24).forEach((r) => {
      doc.setFont("helvetica", r.b ? "bold" : "normal");
      doc.text(doc.splitTextToSize(r.t, boxW - 5)[0], x + 2.5, ry);
      ry += 4.4;
    });
  };
  const clienti = [];
  const pushCli = (nm, ruolo, comp, veic) => {
    clienti.push({ t: [nm, ruolo].filter(Boolean).join(" — "), b: true });
    if (comp) clienti.push({ t: "Compagnia: " + comp });
    if (veic) clienti.push({ t: "Veicolo: " + veic });
  };
  const veicOf = (v) => [[v.marca, v.modello].filter(Boolean).join(" "), v.targa].filter(Boolean).join(" — ");
  const sch = p.assistito_scheda || {};
  const pName = primaryName(p);
  if (pName) {
    pushCli(pName, sch.ruolo || p.assistito_ruolo || "", p.assistito_compagnia || p.compagnia || sch.compagnia || "",
      veicOf({ marca: sch.marca || sch.costruttore || p.marca, modello: sch.modello || p.modello, targa: p.targa || p.assistito_targa || sch.targa }));
  }
  (p.assistiti || []).forEach((a) => {
    const nm = a.nome_assistito || a.assistito_rs || a.ragione_sociale || a.denominazione || [a.cognome, a.nome].filter(Boolean).join(" ");
    if (!nm || (pName && nm.trim().toUpperCase() === pName.trim().toUpperCase())) return;
    const v = a.veicolo || (Array.isArray(a.veicoli) && a.veicoli[0]) || {};
    pushCli(nm, a.ruolo, a.compagnia || a.assicurazione || "", veicOf({ marca: v.marca || a.marca, modello: v.modello || a.modello, targa: v.targa || a.targa || a.veicolo_targa }));
  });
  const controparti = [];
  (p.controparti || []).forEach((c) => {
    const nm = c.ragione_sociale || c.denominazione || [c.cognome, c.nome].filter(Boolean).join(" ");
    if (!nm) return;
    controparti.push({ t: [nm, c.ruolo].filter(Boolean).join(" — "), b: true });
    const v = c.veicolo || (Array.isArray(c.veicoli) && c.veicoli[0]) || {};
    if (c.compagnia || c.assicurazione || v.compagnia) controparti.push({ t: "Compagnia: " + (c.compagnia || c.assicurazione || v.compagnia) });
    const veicLine = veicOf({ marca: c.marca || c.costruttore || v.marca, modello: c.modello || v.modello, targa: c.targa || v.targa });
    if (veicLine) controparti.push({ t: "Veicolo: " + veicLine });
  });
  // Perito Compagnia / Liquidatore / N° Sinistro Responsabile:
  // se RD -> lato cliente (dati assistito); se RCA o altro -> lato controparte.
  {
    const cp0 = (p.controparti && p.controparti[0]) || {};
    const isRD = String(p.tipo || "").trim().toUpperCase() === "RD";
    const perito = isRD ? (p.assistito_perito_compagnia || "") : (cp0.perito_compagnia || "");
    const liquidatore = isRD ? (p.assistito_liquidatore || "") : (cp0.liquidatore || "");
    const nsin = isRD ? (p.assistito_num_sinistro || "") : (cp0.num_sinistro || "");
    const target = isRD ? clienti : controparti;
    if (perito) target.push({ t: "Perito Compagnia: " + perito });
    if (liquidatore) target.push({ t: "Liquidatore: " + liquidatore });
    if (nsin) target.push({ t: "N° Sinistro Resp.: " + nsin });
  }
  boxH = Math.max(54, Math.min(24, Math.max(clienti.length, controparti.length)) * 4.4 + 12);
  drawParties(M, "CLIENTI", clienti);
  drawParties(M + boxW + 6, "CONTROPARTI", controparti);
  y += boxH + 8;

  // ---- Compagnia di gestione (dipende dalla Procedura CARD) ----
  const cp0 = (p.controparti && p.controparti[0]) || {};
  const a0 = (p.assistiti && p.assistiti[0]) || {};
  const card = String(p.procedura_card || "").toUpperCase();
  const useAssistito = card === "CARD" || card === "DOPPIA FIRMA" || card === "SI";
  const gestione = useAssistito
    ? (p.compagnia || p.assistito_compagnia || a0.assicurazione || a0.compagnia || "")
    : (cp0.compagnia || p.cp_compagnia || "");
  doc.setFillColor(230); doc.rect(M, y, cw, 9, "F");
  doc.setDrawColor(0); doc.setLineWidth(0.4); doc.rect(M, y, cw, 9);
  doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(0);
  doc.text(`COMPAGNIA DI GESTIONE: ${gestione || "—"}`, M + 2.5, y + 6);
  y += 9 + 7;

  // ---- Campi configurati (label/value) ----
  doc.setFillColor(0); doc.rect(M, y, cw, 6, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(255);
  doc.text("DATI PRATICA", M + 2.5, y + 4.2); doc.setTextColor(0);
  y += 6;
  const rowH = 8;
  const labelW = 55;
  doc.setLineWidth(0.3);
  keys.forEach((k) => {
    const st = styles[k] || {};
    if (y > H - 24) { doc.addPage(); y = M; }
    const hl = st.highlight ? hexToRgb(st.highlight) : null;
    if (hl) { doc.setFillColor(hl[0], hl[1], hl[2]); doc.rect(M + labelW, y, cw - labelW, rowH, "F"); }
    doc.setDrawColor(0); doc.setTextColor(0);
    doc.rect(M, y, cw, rowH);
    doc.line(M + labelW, y, M + labelW, y + rowH);
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5);
    doc.text(labelOf(k).toUpperCase(), M + 2.5, y + rowH / 2 + 1.5);
    const size = COP_SIZES.includes(Number(st.size)) ? Number(st.size) : 10;
    doc.setFont("helvetica", st.bold ? "bold" : "normal"); doc.setFontSize(size);
    let val = String(copertinaValue(p, k) || "—");
    if (st.upper) val = val.toUpperCase();
    doc.text(doc.splitTextToSize(val, cw - labelW - 5)[0], M + labelW + 2.5, y + rowH / 2 + 1.5);
    y += rowH;
  });

  // ---- Footer ----
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(120);
  doc.text(`Copertina pratica ${numero}`, W / 2, H - 8, { align: "center" });

  return { doc, filename: `Copertina_${(numero || "pratica").replace(/[^\w-]/g, "_")}.pdf` };
}

export function buildCopertina(p, studio = {}, fieldKeys, margins, fieldStyles, pecDate) {
  return buildDoc(p, studio, fieldKeys, margins, fieldStyles, pecDate);
}

export function generateCopertinaPDF(p, studio = {}, fieldKeys) {
  const { doc, filename } = buildDoc(p, studio, fieldKeys);
  doc.save(filename);
}

// Template HTML di default della copertina (usato come seed dell'editor quando non personalizzato).
// Usa le variabili dell'editor lettere (incluse quelle della gestionaria). La variabile
// {{copertina.data_pec}} è posizionabile liberamente dall'utente e viene sostituita a runtime
// con la data inserita nel dialog "Data PEC copertina" prima della generazione (o con "—" se vuota).
export const DEFAULT_COPERTINA_TEMPLATE = [
  '<div style="text-align:center;margin-bottom:6px">',
  '<p style="font-size:16pt;font-weight:bold;margin:0">{{studio.intestazione:upper}}</p>',
  '<p style="font-size:9pt;margin:2px 0">{{studio.indirizzo}} {{studio.numero_civico}} — {{studio.cap}} {{studio.citta}} ({{studio.provincia}})</p>',
  '</div>',
  '<div style="border:2px solid #000;padding:6px 10px;text-align:center;font-size:18pt;font-weight:bold;margin:8px 0">PRATICA N. {{pratica.numero}} — [{{tipo_sinistro}}]</div>',
  '<table style="width:100%;border-collapse:collapse;font-size:11pt;margin-bottom:8px"><tbody><tr>',
  '<td style="width:50%;vertical-align:top;padding:4px"><strong>SINISTRO DEL:</strong> {{pratica.data_sinistro}}</td>',
  '<td style="width:50%;vertical-align:top;padding:4px"><strong>FORNITORE:</strong> ______________________</td>',
  '</tr></tbody></table>',
  '<table style="width:100%;border-collapse:collapse;font-size:11pt"><tbody><tr>',
  '<td style="width:50%;vertical-align:top;border:1px solid #000;padding:6px">',
  '<div style="font-weight:bold;border-bottom:1px solid #000;margin-bottom:4px">CLIENTI</div>',
  '<div>{{clienti.elenco}}</div>',
  '</td>',
  '<td style="width:50%;vertical-align:top;border:1px solid #000;padding:6px">',
  '<div style="font-weight:bold;border-bottom:1px solid #000;margin-bottom:4px">CONTROPARTI</div>',
  '<div>{{controparti.elenco}}</div>',
  '</td>',
  '</tr></tbody></table>',
  '<div style="font-weight:bold;border-bottom:2px solid #000;margin:10px 0 4px;font-size:11pt">DATI ASSICURATIVI DI GESTIONE</div>',
  '<table style="width:100%;border-collapse:collapse;font-size:11pt"><tbody><tr>',
  '<td style="width:60%;vertical-align:top;padding:2px">',
  '<p style="margin:3px 0"><strong>Sinistro n.:</strong> {{gestionaria.num_sinistro}}</p>',
  '<p style="margin:3px 0"><strong>Liquidatore:</strong> {{gestionaria.liquidatore.nominativo}}</p>',
  '<p style="margin:3px 0"><strong>Perito:</strong> {{gestionaria.perito_compagnia.nominativo}}</p>',
  '<p style="margin:3px 0"><strong>Medico:</strong> ______________________</p>',
  '</td>',
  '<td style="width:40%;vertical-align:top;padding:2px">',
  '<p style="margin:3px 0"><strong>Telefono n.:</strong> {{gestionaria.liquidatore_telefono}}</p>',
  '<p style="margin:3px 0"><strong>Fax n.:</strong> ______________</p>',
  '<p style="margin:3px 0"><strong>Telefono n.:</strong> ______________</p>',
  '<p style="margin:3px 0"><strong>Telefono n.:</strong> ______________</p>',
  '</td>',
  '</tr></tbody></table>',
  '<div style="font-weight:bold;border-bottom:2px solid #000;margin:10px 0 4px;font-size:11pt">FATTURE</div>',
  '<table style="width:100%;border-collapse:collapse;font-size:11pt"><tbody><tr>',
  '<td style="padding:2px"><strong>DATA:</strong> ______________</td>',
  '<td style="padding:2px"><strong>FATTURA N.:</strong> ______________</td>',
  '<td style="padding:2px"><strong>DATA ARCHIVIAZIONE:</strong> ______________</td>',
  '</tr></tbody></table>',
  '<table style="width:100%;border-collapse:collapse;font-size:11pt;margin-top:10px"><tbody><tr>',
  '<td style="padding:2px"><strong>STUDIO LEGALE:</strong> ______________</td>',
  '<td style="padding:2px"><strong>TRIBUNALE:</strong> ______________</td>',
  '<td style="padding:2px"><strong>N. RG:</strong> ______________</td>',
  '</tr></tbody></table>',
].join("");
